/**
 * iter53 — Public analytics snapshot page (no auth required)
 * Route: /analytics-snapshot/:token
 *
 * Fetches GET /api/public/analytics-snapshot/:token and renders a read-only
 * mirror of the owner's analytics page.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend,
} from 'recharts';
import {
  Eye, MousePointerClick, Users, TrendingUp, Bot, ExternalLink,
  Camera, Radio, AlertCircle, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Tile = ({ icon: Icon, label, value, sublabel, accent, testId }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${accent || 'text-slate-100'}`} data-testid={testId}>
            {value}
          </p>
          {sublabel && <p className="text-[10px] text-slate-500 mt-0.5">{sublabel}</p>}
        </div>
        <Icon className={`h-5 w-5 ${accent || 'text-slate-500'}`} />
      </div>
    </CardContent>
  </Card>
);

const RANGE_LABELS = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const PublicAnalyticsSnapshotPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSnapshot = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/analytics-snapshot/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (res.status === 410) throw new Error(err.detail || 'This snapshot has expired.');
          if (res.status === 404) throw new Error(err.detail || 'This snapshot is no longer available.');
          throw new Error(err.detail || 'Failed to load snapshot.');
        }
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSnapshot();
  }, [token]);

  const analytics = useMemo(() => data?.analytics || {}, [data]);
  const summary = useMemo(() => analytics.summary || {}, [analytics]);
  const timeseries = useMemo(() => analytics.timeseries || [], [analytics]);
  const chartData = useMemo(
    () => timeseries.map((d) => ({
      date: (d.date || '').slice(5),
      impressions: d.impressions,
      clicks: d.clicks,
    })),
    [timeseries],
  );

  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#0A1024] flex items-center justify-center"
        data-testid="snapshot-loading"
      >
        <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen bg-[#0A1024] flex items-center justify-center p-6"
        data-testid="snapshot-error"
      >
        <Card className="bg-[#1A2440] border-amber-800 max-w-md w-full">
          <CardContent className="p-6 text-center space-y-3">
            <AlertCircle className="h-10 w-10 text-amber-400 mx-auto" />
            <h1 className="text-xl font-semibold text-slate-100">Snapshot unavailable</h1>
            <p className="text-sm text-slate-400">{error}</p>
            <a
              href="https://dotbox.ai"
              className="inline-block text-xs text-slate-500 hover:text-slate-300 mt-4"
            >
              Powered by <span className="text-slate-300">dot<span className="text-rose-500">box.ai</span></span>
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0A1024] text-slate-100 p-4 md:p-8"
      data-testid="public-snapshot-page"
    >
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-[#1F2A4D] pb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {data.mode === 'live' ? (
                <Badge variant="outline" className="text-[10px] border-emerald-800 bg-emerald-950 text-emerald-300">
                  <Radio className="h-2.5 w-2.5 mr-1" /> LIVE
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] border-blue-800 bg-blue-950 text-blue-300">
                  <Camera className="h-2.5 w-2.5 mr-1" /> SNAPSHOT
                </Badge>
              )}
              <span className="text-xs text-slate-500">
                {RANGE_LABELS[data.range] || data.range}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-100">
              <span className="text-rose-400">{data.widget_name}</span> · Analytics
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              {data.mode === 'frozen' ? (
                <>Captured on {formatDate(data.captured_at || data.created_at)}</>
              ) : (
                <>Shared on {formatDate(data.created_at)} · always up to date</>
              )}
            </p>
          </div>
          <a
            href="https://dotbox.ai"
            className="text-xs text-slate-500 hover:text-slate-300 self-start md:self-center"
            data-testid="snapshot-branding"
          >
            Powered by <span className="text-slate-200 font-semibold">dot<span className="text-rose-500">box.ai</span></span>
          </a>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile icon={Eye} label="Impressions" value={summary.impressions ?? 0}
                sublabel="Widget loads" accent="text-emerald-400" testId="snap-impressions" />
          <Tile icon={Users} label="Unique visitors" value={summary.unique_visitors ?? 0}
                sublabel="By hashed IP" accent="text-blue-400" testId="snap-unique" />
          <Tile icon={MousePointerClick} label="Clicks" value={summary.clicks ?? 0}
                sublabel="Content clicks" accent="text-amber-400" testId="snap-clicks" />
          <Tile icon={TrendingUp} label="Click-through rate" value={`${summary.ctr_percent ?? 0}%`}
                sublabel="Clicks ÷ impressions" accent="text-rose-400" testId="snap-ctr" />
        </div>

        {summary.bot_impressions > 0 && !data.include_bots && (
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-950/40 border border-slate-700/50 rounded text-xs text-slate-400">
            <Bot className="h-3.5 w-3.5" />
            <span>
              <strong className="text-slate-200">{summary.bot_impressions}</strong> bot impression{summary.bot_impressions === 1 ? '' : 's'} excluded from these totals.
            </span>
          </div>
        )}

        {/* Time-series chart */}
        <Card className="bg-[#1A2440] border-[#2A3654]">
          <CardHeader>
            <CardTitle className="text-slate-100 text-base">Impressions & Clicks over time</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-8">No activity in this range.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2A4D" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: '#0F1730', border: '1px solid #1F2A4D', borderRadius: '6px' }}
                    labelStyle={{ color: '#f1f5f9', fontSize: '12px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="impressions" stroke="#10B981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="clicks" stroke="#F59E0B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Referrers + Content two-up */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#1A2440] border-[#2A3654]">
            <CardHeader>
              <CardTitle className="text-slate-100 text-base flex items-center gap-2">
                <ExternalLink className="h-4 w-4" /> Top embedding sites
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(analytics.referrers || []).length === 0 ? (
                <p className="text-xs text-slate-500 italic py-4 text-center">No referrers yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {analytics.referrers.map((r) => (
                    <div
                      key={r.host}
                      className="flex items-center justify-between px-3 py-2 bg-[#0F1730] rounded border border-[#1F2A4D]"
                    >
                      <span className="text-sm text-slate-200 truncate">{r.host}</span>
                      <Badge variant="outline" className="text-[10px]">{r.impressions}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#1A2440] border-[#2A3654]">
            <CardHeader>
              <CardTitle className="text-slate-100 text-base flex items-center gap-2">
                <MousePointerClick className="h-4 w-4" /> Top clicked content
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(analytics.top_content || []).length === 0 ? (
                <p className="text-xs text-slate-500 italic py-4 text-center">No clicks yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {analytics.top_content.map((c) => (
                    <div
                      key={c.content_id}
                      className="flex items-center justify-between px-3 py-2 bg-[#0F1730] rounded border border-[#1F2A4D]"
                    >
                      <div className="min-w-0 mr-2">
                        <p className="text-sm text-slate-200 truncate">{c.title}</p>
                        {c.source_name && (
                          <p className="text-[10px] text-slate-500">{c.source_name}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-[10px] shrink-0">{c.clicks}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Device / browser breakdown */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#1A2440] border-[#2A3654]">
            <CardHeader>
              <CardTitle className="text-slate-100 text-base">Device breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {(analytics.device_breakdown || []).length === 0 ? (
                <p className="text-xs text-slate-500 italic py-4 text-center">No data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={analytics.device_breakdown} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2A4D" />
                    <XAxis dataKey="value" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} />
                    <Tooltip
                      contentStyle={{ background: '#0F1730', border: '1px solid #1F2A4D', borderRadius: '6px' }}
                      labelStyle={{ color: '#f1f5f9', fontSize: '12px' }}
                    />
                    <Bar dataKey="count" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card className="bg-[#1A2440] border-[#2A3654]">
            <CardHeader>
              <CardTitle className="text-slate-100 text-base">Browser breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {(analytics.browser_breakdown || []).length === 0 ? (
                <p className="text-xs text-slate-500 italic py-4 text-center">No data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={analytics.browser_breakdown} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2A4D" />
                    <XAxis dataKey="value" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} />
                    <Tooltip
                      contentStyle={{ background: '#0F1730', border: '1px solid #1F2A4D', borderRadius: '6px' }}
                      labelStyle={{ color: '#f1f5f9', fontSize: '12px' }}
                    />
                    <Bar dataKey="count" fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="text-center pt-6 pb-4 border-t border-[#1F2A4D]">
          <p className="text-[11px] text-slate-500">
            This is a read-only analytics snapshot.{' '}
            <a href="https://dotbox.ai" className="text-slate-300 hover:text-white underline">
              Create your own at dotbox.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicAnalyticsSnapshotPage;
